/**
 * Makes model-written prose presentable before a reader sees it.
 *
 * The analyst is told to write business titles and rounded figures, and does
 * not always do so. These rules are deterministic so the screen, the email and
 * the exports (`presentation.py` mirrors them) never show an engine detail.
 */
import { humanise } from "./chartOptions";
import { roundNumbersInText } from "./numberFormat";

/**
 * A qualified member written in prose: `fact_interaction.blind_transferred_rate`
 * or `dim_agent.name`. Bounded so an email address, a URL, a file name such as
 * report.pdf, or "e.g." is left alone: a member has a fact/dim cube or an
 * underscore somewhere, and is not glued to `@`, `/` or a further dotted part.
 */
const QUALIFIED_MEMBER =
  /(?<![\w.@/:-])[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+(?![\w@/(-]|\.[a-z0-9])/g;
const CUBE_PREFIX = /^(?:fact|dim)(?:_|\.)/;

function isMember(token: string): boolean {
  return CUBE_PREFIX.test(token) || token.includes("_");
}

/** Replaces each qualified member in the text with its business title. */
export function humaniseMembersInText(text: string): string {
  return text.replace(QUALIFIED_MEMBER, (token) =>
    isMember(token) ? humanise(token) : token,
  );
}

/** Document section kinds as the approver reads them; mirrors SECTION_LABELS. */
const SECTION_LABELS: Record<string, string> = {
  headline: "Headline",
  kpi: "KPI band",
  panel: "Chart",
  table: "Table",
  findings: "Findings",
};

/**
 * A proposed section line that opens with its kind: "2. kpi — KPI band: …".
 * Only at the start of a line, so a sentence that uses the word is untouched.
 */
const SECTION_LINE =
  /^(\s*(?:\d+[.)]|[-*])?\s*)(headline|kpi|panel|table|findings)\s+[—–-]\s+(.*)$/gm;

/** Proposed-structure lines with each kind named as the approver reads it. */
export function labelSectionKinds(text: string): string {
  return text.replace(SECTION_LINE, (_line, lead: string, kind: string, rest: string) => {
    const label = SECTION_LABELS[kind] ?? kind;
    // The planner often repeats the kind in the title: "kpi — KPI band: …".
    const repeated = [label, kind].find((prefix) =>
      rest.toLowerCase().startsWith(prefix.toLowerCase()),
    );
    const title = repeated ? rest.slice(repeated.length).replace(/^[\s:—–-]+/, "") : rest;
    return `${lead}${label} — ${title || rest}`;
  });
}

/** Every prose rule a reader-facing sentence or table cell goes through. */
export function readableText(text: string): string {
  return humaniseMembersInText(roundNumbersInText(text));
}
